import { createContext, useContext, useEffect, useState } from 'react';
import defaults from './defaults.json';

export type Content = typeof defaults;

/** Where the editor/site read & write content. Same-origin (nginx proxies /api). */
export const API_BASE = '/api';

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/** Deep-merge an override object onto a base, so partial overrides keep defaults. */
export function deepMerge<T>(base: T, override: unknown): T {
  if (Array.isArray(base)) {
    // Arrays are replaced wholesale when an override array is provided.
    return (Array.isArray(override) ? override : base) as T;
  }
  if (isObject(base) && isObject(override)) {
    const out: Record<string, unknown> = { ...base };
    for (const key of Object.keys(base)) {
      if (key in override) {
        out[key] = deepMerge((base as Record<string, unknown>)[key], override[key]);
      }
    }
    return out as T;
  }
  return (override === undefined ? base : (override as T));
}

export const ContentContext = createContext<Content>(defaults);

export function ContentProvider({ children }: { children: React.ReactNode }) {
  const [content, setContent] = useState<Content>(defaults);

  useEffect(() => {
    let active = true;
    fetch(`${API_BASE}/content`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (active && data && data.content) {
          setContent(deepMerge(defaults, data.content));
        }
      })
      .catch(() => {
        /* backend unavailable → keep built-in defaults */
      });
    return () => {
      active = false;
    };
  }, []);

  return <ContentContext.Provider value={content}>{children}</ContentContext.Provider>;
}

export function useContent(): Content {
  return useContext(ContentContext);
}

/** Spread onto an element to render a (possibly inline-HTML) content string. */
export function html(value: string) {
  return { dangerouslySetInnerHTML: { __html: value ?? '' } };
}

export { defaults };
